import {
  type ESocialNetwork,
  type ISocialPostSuggestion,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import { type IFacebookScoredProduct, type IFacebookState } from './types';

export const preloadedState: IFacebookState = {
  inProcess: false,
  historyInProcess: false,
  productsInProcess: false,
  activeNetworks: [],
  suggestion: null,
  history: [],
  scoredProducts: null,
};

const facebookSlice = createSlice({
  name: 'Facebook',
  initialState: preloadedState,
  reducers: {
    facebookActiveNetworksLoad(state) {
      state.inProcess = true;
    },
    facebookActiveNetworksLoadResponse: {
      reducer(
        state,
        action: { payload: { activeNetworks: ESocialNetwork[] } },
      ) {
        state.activeNetworks = action.payload.activeNetworks;
        state.inProcess = false;
      },
      prepare(activeNetworks: ESocialNetwork[]) {
        return { payload: { activeNetworks } };
      },
    },
    facebookSuggestionLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        country: string,
        socialNetwork: ESocialNetwork,
        productUUID?: string,
      ) {
        return { payload: { country, socialNetwork, productUUID } };
      },
    },
    facebookSuggestionLoadResponse: {
      reducer(
        state,
        action: { payload: { suggestion: ISocialPostSuggestion | null } },
      ) {
        state.inProcess = false;
        state.suggestion = action.payload.suggestion;
      },
      prepare(suggestion: ISocialPostSuggestion | null) {
        return { payload: { suggestion } };
      },
    },
    facebookSuggestionPublish: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        id: number,
        text: string,
        country: string,
        socialNetwork: ESocialNetwork,
        productUUID: string,
      ) {
        return { payload: { id, text, country, socialNetwork, productUUID } };
      },
    },
    facebookSuggestionPublishResponse: {
      reducer(
        state,
        action: { payload: { suggestion: ISocialPostSuggestion | null } },
      ) {
        state.inProcess = false;
        state.suggestion = action.payload.suggestion;
        if (action.payload.suggestion) {
          state.history = state.history.map(
            (item: ISocialPostSuggestion): ISocialPostSuggestion =>
              item.id === action.payload.suggestion!.id
                ? action.payload.suggestion!
                : item,
          );
        }
      },
      prepare(suggestion: ISocialPostSuggestion | null) {
        return { payload: { suggestion } };
      },
    },
    facebookSuggestionSkip: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(id: number) {
        return { payload: { id } };
      },
    },
    facebookSuggestionSkipResponse: {
      reducer(
        state,
        action: { payload: { suggestion: ISocialPostSuggestion | null } },
      ) {
        state.inProcess = false;
        state.suggestion = action.payload.suggestion;
        if (action.payload.suggestion) {
          state.history = state.history.map(
            (item: ISocialPostSuggestion): ISocialPostSuggestion =>
              item.id === action.payload.suggestion!.id
                ? action.payload.suggestion!
                : item,
          );
        }
      },
      prepare(suggestion: ISocialPostSuggestion | null) {
        return { payload: { suggestion } };
      },
    },
    facebookHistoryLoad(state) {
      state.historyInProcess = true;
    },
    facebookHistoryLoadResponse: {
      reducer(
        state,
        action: { payload: { history: ISocialPostSuggestion[] } },
      ) {
        state.historyInProcess = false;
        state.history = action.payload.history;
      },
      prepare(history: ISocialPostSuggestion[]) {
        return { payload: { history } };
      },
    },
    facebookScoredProductsLoad: {
      reducer(state) {
        state.productsInProcess = true;
        state.scoredProducts = null;
      },
      prepare(country: string) {
        return { payload: { country } };
      },
    },
    facebookScoredProductsLoadResponse: {
      reducer(
        state,
        action: { payload: { scoredProducts: IFacebookScoredProduct[] } },
      ) {
        state.productsInProcess = false;
        state.scoredProducts = action.payload.scoredProducts;
      },
      prepare(scoredProducts: IFacebookScoredProduct[]) {
        return { payload: { scoredProducts } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Facebook'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  facebookActiveNetworksLoad,
  facebookActiveNetworksLoadResponse,
  facebookSuggestionLoad,
  facebookSuggestionLoadResponse,
  facebookSuggestionPublish,
  facebookSuggestionPublishResponse,
  facebookSuggestionSkip,
  facebookSuggestionSkipResponse,
  facebookHistoryLoad,
  facebookHistoryLoadResponse,
  facebookScoredProductsLoad,
  facebookScoredProductsLoadResponse,
} = facebookSlice.actions;
export default facebookSlice.reducer;
